<template>
  <div
    data-testid="bank-statement-row"
    class="group/row group grid lg:grid-cols-[40rem_minmax(0,1fr)] 2xl:grid-cols-[minmax(40rem,1fr)_minmax(0,1fr)]"
  >
    <div
      class="relative grid grid-cols-[5.5rem_minmax(0,1fr)_auto] gap-x-4 bg-[#fffdf8] py-3 pl-8 pr-5 font-mono text-[13px] leading-snug text-walnut-900 transition-colors after:absolute after:bottom-0 after:left-8 after:right-5 after:border-b after:border-dashed after:border-hazel-300 lg:bg-transparent lg:group-hover:bg-hazel-50"
      :class="{ 'text-walnut-400': status === 'ignored' }"
    >
      <div class="tabular-nums">
        <p>{{ formatDate(entry.bookedDate, DateTime.DATE_SHORT) }}</p>
        <p v-if="entry.valueDate" class="text-[11px] text-walnut-500">
          {{ formatDate(entry.valueDate, DateTime.DATE_SHORT) }}
        </p>
      </div>
      <div class="min-w-0">
        <p class="truncate font-semibold uppercase tracking-wide">
          <span :class="nameMatches ? exactMark : undefined">{{
            entry.counterpartyName || t('unknownCounterparty')
          }}</span>
        </p>
        <p class="whitespace-pre-wrap break-words">
          <template v-if="highlightedRemittance.length">
            <span
              v-for="(part, index) in highlightedRemittance"
              :key="index"
              :class="part.highlight ? exactMark : undefined"
              >{{ part.text }}</span
            >
          </template>
          <span v-else class="italic text-walnut-500">{{ t('noRemittance') }}</span>
        </p>
        <p
          v-if="entry.counterpartyIban || showAccountIban || showLegalEntity"
          class="text-[11px] text-walnut-500"
        >
          <span
            v-if="entry.counterpartyIban"
            :class="knownIban ? exactMark : undefined"
            :title="knownIban ? t('knownIban') : undefined"
            >{{ entry.counterpartyIban }}</span
          >
          <span v-if="knownIban" class="ml-1 normal-case">{{ t('knownIban') }}</span>
          <span v-if="showAccountIban"> → {{ entry.accountIban }}</span>
          <span v-if="showLegalEntity"> · {{ entry.legalEntity.name }}</span>
        </p>
      </div>
      <p
        class="text-right font-semibold tabular-nums"
        :class="
          status === 'ignored' ? undefined : entry.amount < 0 ? 'text-red-800' : 'text-green-900'
        "
      >
        <span :class="amountExact ? exactMark : amountNear ? nearMark : undefined">{{
          formatCurrency(entry.amount)
        }}</span>
      </p>
    </div>

    <div class="flex pb-3 lg:py-1.5 lg:pl-4">
      <div
        class="flex w-full flex-col gap-1 rounded-md border border-l-4 border-obsidian-100 bg-white px-4 py-2 shadow-sm transition-shadow group-hover:shadow-md"
        :class="[statusStyle.accent, { 'bg-obsidian-50': status === 'ignored' }]"
      >
        <div class="flex min-h-7 flex-wrap items-center gap-2">
          <Badge :variant="statusStyle.variant" :icon="statusStyle.icon">{{ statusLabel }}</Badge>
          <Badge
            v-if="entry.unconfirmedTarget || suggestion?.unconfirmedTarget"
            variant="problem"
            :icon="AlertTriangleIcon"
            :title="t('unconfirmedTarget')"
          >
            {{ t('unconfirmedShort') }}
          </Badge>
          <Badge
            v-if="isCollectiveSuggestion"
            variant="info"
            :icon="LayersIcon"
            :title="t('collectiveSuggestionHint')"
          >
            {{ t('collectiveSuggestion', { count: suggestion?.targets.length ?? 0 }) }}
          </Badge>

          <div
            class="ml-auto flex flex-wrap items-center justify-end gap-2 transition-opacity lg:opacity-0 lg:group-focus-within:opacity-100 lg:group-hover:opacity-100"
            :class="{ 'lg:!opacity-100': isConfirming }"
          >
            <template v-if="status === 'suggested'">
              <Select
                v-if="suggestion?.needsCounterparty"
                v-model="counterpartyType"
                :options="counterpartyOptions"
                :placeholder="t('counterpartyPlaceholder')"
                size="sm"
                class="w-36"
              />
              <CVButton
                v-if="canConfirm"
                size="xs"
                variant="default"
                :disabled="confirmDisabled"
                :is-loading="isConfirming"
                @click="handleConfirmSuggestion"
              >
                {{ suggestion ? t(`confirmAction.${suggestion.action}`) : '' }}
              </CVButton>
            </template>
            <CVButton
              v-if="!entry.action || canConfirm"
              size="xs"
              :variant="statusStyle.secondaryVariant"
              @click="
                entry.action === BankStatementAction.IGNORE
                  ? emit('confirm', { action: null })
                  : emit('chooseTarget', entry)
              "
            >
              {{ secondaryActionLabel }}
            </CVButton>
            <CVButton
              v-if="canConfirm && isConfirmedLink"
              size="iconXs"
              variant="ghost"
              :title="t('undoHint')"
              :aria-label="t('undo')"
              :disabled="isConfirming"
              @click="handleUndo"
            >
              <Undo2Icon class="size-3.5 text-obsidian-500" />
            </CVButton>
            <CVButton
              v-if="canConfirm && entry.action !== BankStatementAction.IGNORE"
              size="xs"
              variant="ghost"
              class="gap-1 px-2 text-obsidian-500"
              :disabled="isConfirming"
              @click="handleIgnore"
            >
              <EyeOffIcon class="size-3.5" />
              {{ t('ignore') }}
            </CVButton>
          </div>
          <FloatingComments entity-type="BankStatementEntry" :entity-id="entry.id" />
        </div>

        <template v-if="status === 'suggested' && suggestion">
          <p v-for="(parts, lineIndex) in targetLines" :key="lineIndex" class="text-sm">
            <template v-for="(part, index) in parts.parts" :key="index">
              <span v-if="index > 0" class="text-obsidian-400"> · </span>
              <span v-if="part.label" class="mr-1 text-obsidian-500">{{ part.label }}</span>
              <router-link v-if="part.to" :to="part.to" class="text-link font-medium">{{
                part.value
              }}</router-link>
              <span v-else class="font-medium">{{ part.value }}</span>
            </template>
            <span v-if="parts.parts.length" class="text-obsidian-400"> · </span>
            <span v-if="parts.amountLabel" class="mr-1 text-obsidian-500">{{
              parts.amountLabel
            }}</span>
            <span class="font-semibold tabular-nums">{{ formatCurrency(parts.amount) }}</span>
          </p>
          <p v-if="isCollectiveSuggestion" class="text-sm tabular-nums text-obsidian-500">
            {{ t('collectiveSum', { sum: formatCurrency(targetSum) }) }}
            <span v-if="targetDifference > 0">
              · {{ t('collectiveDifference', { difference: formatCurrency(targetDifference) }) }}
            </span>
            <span v-else-if="targetDifference < 0"> · {{ t('collectivePartial') }}</span>
          </p>
        </template>
        <p
          v-if="
            status === 'suggested' &&
            (suggestion?.target.counterpartyName || suggestion?.target.counterpartyType)
          "
          class="flex flex-wrap items-center gap-2 text-sm font-medium"
        >
          <span v-if="suggestion?.target.counterpartyName">{{
            suggestion.target.counterpartyName
          }}</span>
          <CounterpartyTypeBadge
            v-if="suggestion?.target.counterpartyType"
            :counterparty-type="suggestion.target.counterpartyType"
            size="sm"
          />
        </p>
        <p v-else-if="entry.isCollectivePayment && entry.paymentId" class="text-sm">
          <span class="text-obsidian-500">{{ statusHint }} · </span>
          <router-link
            :to="{ name: 'payment', params: { id: entry.paymentId } }"
            class="text-link font-medium"
            >{{ t('distribute') }}</router-link
          >
        </p>
        <p v-else-if="status !== 'suggested'" class="text-sm text-obsidian-500">
          {{ statusHint }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import Badge from '@/components/Badge.vue';
import CounterpartyTypeBadge from '@/components/badges/CounterpartyTypeBadge.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import FloatingComments from '@/components/comments/FloatingComments.vue';
import Select from '@/components/Select.vue';
import {
  getAmountDifference,
  sumOpenAmounts,
  toPaymentTarget,
} from '@/api/bank-statements.service';
import { formatCurrency } from '@/utils/format-numbers';
import { formatDate } from '@/hooks/use-formatted-date';
import { DateTime } from 'luxon';
import {
  AlertTriangleIcon,
  CheckIcon,
  CircleDashedIcon,
  EyeOffIcon,
  LayersIcon,
  SearchIcon,
  SparklesIcon,
  Undo2Icon,
} from 'lucide-vue-next';
import { computed, ref, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import type { RouteLocationRaw } from 'vue-router';
import type { BadgeVariant } from '@carvia/theme/lib/cva/badge';
import type { button } from '@carvia/theme/lib/cva/button';
import type { CvaComponentProps } from '@carvia/theme/lib/cva/CvaComponentProps.ts';
import {
  BankStatementAction,
  BankStatementMatchCertainty,
  BankStatementOpenAmountType,
  BankStatementSuggestionSource,
  CounterpartyType,
  type BankStatementEntryDto,
  type BankStatementOpenAmountDto,
  type BankStatementSuggestionDto,
  type ConfirmBankStatementActionDto,
} from '@carvia/ros-client-types';

const props = defineProps<{
  entry: BankStatementEntryDto;
  canConfirm: boolean;
  isConfirming?: boolean;
  showAccountIban?: boolean;
  showLegalEntity?: boolean;
}>();

const emit = defineEmits<{
  (e: 'confirm', body: ConfirmBankStatementActionDto): void;
  (e: 'chooseTarget', entry: BankStatementEntryDto): void;
}>();

const { t } = useI18n();
const counterpartyType = ref<CounterpartyType | null>(
  props.entry.suggestion?.target.counterpartyType ?? null,
);

type ButtonVariant = NonNullable<CvaComponentProps<typeof button>['variant']>;
type RowStatus = 'suggested' | 'candidates' | 'none' | 'confirmed' | 'ignored';

const STATUS_STYLES: Record<
  RowStatus,
  { icon: Component; variant: BadgeVariant; accent: string; secondaryVariant: ButtonVariant }
> = {
  suggested: {
    icon: SparklesIcon,
    variant: 'info',
    accent: 'border-l-blue-400',
    secondaryVariant: 'ghost',
  },
  candidates: {
    icon: SearchIcon,
    variant: 'warning',
    accent: 'border-l-yellow-500',
    secondaryVariant: 'default',
  },
  none: {
    icon: CircleDashedIcon,
    variant: 'problem',
    accent: 'border-l-red-300',
    secondaryVariant: 'outline',
  },
  confirmed: {
    icon: CheckIcon,
    variant: 'success',
    accent: 'border-l-green-700',
    secondaryVariant: 'ghost',
  },
  ignored: {
    icon: EyeOffIcon,
    variant: 'obsidian',
    accent: 'border-l-obsidian-300',
    secondaryVariant: 'outline',
  },
};

const suggestion = computed(() => props.entry.suggestion);
const status = computed<RowStatus>(() => {
  if (props.entry.action === BankStatementAction.IGNORE) return 'ignored';
  if (props.entry.action) return 'confirmed';
  if (suggestion.value?.certainty === BankStatementMatchCertainty.HIGH) return 'suggested';
  // A known IBAN preselects the payer's open amounts even when the last is paid partially.
  if (suggestion.value?.source === BankStatementSuggestionSource.KNOWN_IBAN) return 'suggested';
  return suggestion.value ? 'candidates' : 'none';
});
const statusStyle = computed(() => STATUS_STYLES[status.value]);
const statusLabel = computed(() => {
  if (props.entry.isCollectivePayment) return t('confirmed.COLLECTIVE');
  return props.entry.action ? t(`confirmed.${props.entry.action}`) : t(`status.${status.value}`);
});
const statusHint = computed(() => {
  if (props.entry.confirmedAt && props.entry.action) {
    return t('confirmedAt', { date: formatDate(props.entry.confirmedAt, DateTime.DATE_SHORT) });
  }
  return t(`hint.${status.value}`);
});
const secondaryActionLabel = computed(() => {
  if (props.entry.action === BankStatementAction.IGNORE) return t('unignore');
  if (props.entry.action) return t('change');
  return status.value === 'candidates' ? t('showCandidates') : t('choose');
});

const knownIban = computed(
  () => !!suggestion.value?.targets.some((target) => target.highlights?.knownIban),
);
const isCollectiveSuggestion = computed(() => (suggestion.value?.targets.length ?? 0) > 1);
const targetLines = computed(() =>
  (suggestion.value?.targets ?? []).map((target) => ({
    parts: getTargetParts(target),
    amount: target.amount,
    amountLabel: getAmountLabel(target.type),
  })),
);
const targetSum = computed(() => sumOpenAmounts(suggestion.value?.targets ?? []));
const targetDifference = computed(() => getAmountDifference(props.entry.amount, targetSum.value));

function getTargetParts(target: BankStatementOpenAmountDto) {
  const parts: { label?: string; value: string | number; to?: RouteLocationRaw }[] = [];
  if (target.booking) {
    parts.push({
      label: t('booking'),
      value: target.booking.bookingNumber,
      to: { name: 'bookingEdit', params: { id: target.booking.id } },
    });
  }
  if (target.invoice?.completeInvoiceNumber) {
    parts.push({
      label: t('invoice'),
      value: target.invoice.completeInvoiceNumber,
      to: { name: 'invoice', params: { id: target.invoice.id } },
    });
  }
  if (target.settlementDetail) {
    parts.push({ label: t('payoutLabel'), value: target.settlementDetail.batch.batchNumber });
  }
  return parts;
}

const getAmountLabel = (type: BankStatementOpenAmountType) => {
  if (type === BankStatementOpenAmountType.PAYMENT) return t('amountLabels.payment');
  if (type === BankStatementOpenAmountType.INVOICE || type === BankStatementOpenAmountType.BOOKING)
    return t('amountLabels.open');
  return null;
};

const exactMark = 'rounded-sm bg-green-100 px-0.5 text-green-900';
const nearMark = 'rounded-sm bg-yellow-200 px-0.5 text-walnut-900';
const amountDelta = computed(() => {
  if (!suggestion.value) return null;
  return Math.abs(Math.round(props.entry.amount * 100) - Math.round(targetSum.value * 100));
});
const amountExact = computed(() => amountDelta.value === 0);
const amountNear = computed(
  () => amountDelta.value != null && amountDelta.value > 0 && amountDelta.value <= 100,
);
const nameMatches = computed(() => {
  const targetName = suggestion.value?.target.counterpartyName
    ?.trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  const entryName = props.entry.counterpartyName?.trim().toLowerCase().replace(/\s+/g, ' ');
  return (
    !!targetName &&
    !!entryName &&
    (targetName.includes(entryName) || entryName.includes(targetName))
  );
});

const confirmDisabled = computed(
  () => !suggestion.value || (suggestion.value.needsCounterparty && !counterpartyType.value),
);

const counterpartyOptions = [
  { label: t('counterpartyTypes.CUSTOMER'), value: CounterpartyType.CUSTOMER },
  { label: t('counterpartyTypes.AGENCY'), value: CounterpartyType.AGENCY },
  { label: t('counterpartyTypes.OTHER'), value: CounterpartyType.OTHER },
];

const highlightedRemittance = computed(() => {
  const text = props.entry.remittance || '';
  if (!text) return [];
  return highlightTokens(text, (suggestion.value?.targets ?? []).flatMap(getMatchTokens));
});

const handleConfirmSuggestion = () => {
  const suggestion = props.entry.suggestion;
  if (!suggestion) return;
  emit('confirm', buildConfirmBody(suggestion, counterpartyType.value));
};

const handleIgnore = () => {
  emit('confirm', { action: BankStatementAction.IGNORE });
};

const isConfirmedLink = computed(
  () => !!props.entry.action && props.entry.action !== BankStatementAction.IGNORE,
);

const handleUndo = () => {
  emit('confirm', { action: null });
};

function buildConfirmBody(
  suggestion: BankStatementSuggestionDto,
  selectedCounterparty: CounterpartyType | null,
): ConfirmBankStatementActionDto {
  if (suggestion.targets.length > 1) {
    return { action: suggestion.action, targets: suggestion.targets.map(toPaymentTarget) };
  }
  return {
    action: suggestion.action,
    invoiceId: suggestion.target.invoice?.id,
    bookingId: suggestion.target.booking?.id,
    paymentId: suggestion.target.payment?.id,
    settlementDetailId: suggestion.target.settlementDetail?.id,
    counterpartyType: suggestion.target.counterpartyType ?? selectedCounterparty ?? undefined,
  };
}

function getMatchTokens(target: BankStatementOpenAmountDto): string[] {
  const tokens = [target.invoice?.completeInvoiceNumber, target.payment?.referenceId];
  if (target.booking) tokens.push(String(target.booking.bookingNumber));
  return tokens.filter((token): token is string => !!token && token.length >= 4);
}

function highlightTokens(text: string, matchTokens: string[]) {
  const tokens = [...new Set(matchTokens)].sort((a, b) => b.length - a.length);
  if (tokens.length === 0) return [{ text, highlight: false }];
  const pattern = new RegExp(tokens.map(escapeRegExp).join('|'), 'gi');
  const parts: { text: string; highlight: boolean }[] = [];
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) parts.push({ text: text.slice(cursor, index), highlight: false });
    parts.push({ text: match[0], highlight: true });
    cursor = index + match[0].length;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor), highlight: false });
  return parts.length ? parts : [{ text, highlight: false }];
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
</script>

<i18n lang="json">
{
  "en": {
    "knownIban": "Known IBAN",
    "collectiveSuggestion": "Collective payment · {count} items",
    "collectiveSuggestionHint": "One payment, allocated to the oldest due item first",
    "collectiveSum": "Sum {sum}",
    "collectiveDifference": "difference {difference} stays unallocated (Problems)",
    "collectivePartial": "the last item is paid only partially",
    "unknownCounterparty": "Unknown counterparty",
    "noRemittance": "No remittance",
    "unconfirmedTarget": "Booking is not confirmed yet — payment will still be allocated.",
    "unconfirmedShort": "Booking unconfirmed",
    "showCandidates": "Show candidates",
    "booking": "Booking",
    "invoice": "Invoice",
    "payoutLabel": "Payout",
    "amountLabels": {
      "open": "open",
      "payment": "Payment"
    },
    "choose": "Choose target",
    "ignore": "Ignore",
    "change": "Change",
    "unignore": "Un-ignore",
    "undo": "Undo",
    "undoHint": "Undo and move back to \"To match\" — a created payment stays",
    "distribute": "Open payment →",
    "counterpartyPlaceholder": "Counterparty",
    "confirmedAt": "Confirmed on {date}",
    "status": {
      "suggested": "Suggestion",
      "candidates": "Possible matches",
      "none": "No match"
    },
    "hint": {
      "candidates": "Several open amounts could fit — please review.",
      "none": "No matching open amount found.",
      "confirmed": "Assigned",
      "ignored": "Will not be assigned."
    },
    "confirmAction": {
      "CREATE_PAYMENT": "Create payment",
      "LINK_PAYMENT": "Link",
      "LINK_SETTLEMENT": "Link"
    },
    "confirmed": {
      "CREATE_PAYMENT": "Payment created",
      "LINK_PAYMENT": "Payment linked",
      "LINK_SETTLEMENT": "Merchant payout linked",
      "IGNORE": "Ignored",
      "COLLECTIVE": "Collective payment created"
    }
  },
  "de": {
    "knownIban": "Bekannte IBAN",
    "collectiveSuggestion": "Sammelzahlung · {count} Posten",
    "collectiveSuggestionHint": "Eine Zahlung, verteilt nach ältester Fälligkeit zuerst",
    "collectiveSum": "Summe {sum}",
    "collectiveDifference": "Differenz {difference} bleibt unzugeordnet (Probleme)",
    "collectivePartial": "der letzte Posten wird nur teilweise bezahlt",
    "unknownCounterparty": "Unbekannter Auftraggeber",
    "noRemittance": "Kein Verwendungszweck",
    "unconfirmedTarget": "Buchung noch nicht bestätigt — Zahlung wird trotzdem zugeordnet.",
    "unconfirmedShort": "Buchung unbestätigt",
    "showCandidates": "Kandidaten anzeigen",
    "booking": "Buchung",
    "invoice": "Rechnung",
    "payoutLabel": "Auszahlung",
    "amountLabels": {
      "open": "offen",
      "payment": "Zahlung"
    },
    "choose": "Ziel wählen",
    "ignore": "Ignorieren",
    "change": "Ändern",
    "unignore": "Wieder öffnen",
    "undo": "Rückgängig",
    "undoHint": "Rückgängig machen und zurück zu \"Offen\" — eine angelegte Zahlung bleibt bestehen",
    "distribute": "Zahlung öffnen →",
    "counterpartyPlaceholder": "Gegenpartei",
    "confirmedAt": "Bestätigt am {date}",
    "status": {
      "suggested": "Vorschlag",
      "candidates": "Mögliche Treffer",
      "none": "Kein Treffer"
    },
    "hint": {
      "candidates": "Mehrere offene Posten kommen infrage — bitte prüfen.",
      "none": "Kein passender offener Posten gefunden.",
      "confirmed": "Zugeordnet",
      "ignored": "Wird nicht zugeordnet."
    },
    "confirmAction": {
      "CREATE_PAYMENT": "Zahlung anlegen",
      "LINK_PAYMENT": "Verknüpfen",
      "LINK_SETTLEMENT": "Verknüpfen"
    },
    "confirmed": {
      "CREATE_PAYMENT": "Zahlung angelegt",
      "LINK_PAYMENT": "Zahlung verknüpft",
      "LINK_SETTLEMENT": "Händlerauszahlung verknüpft",
      "IGNORE": "Ignoriert",
      "COLLECTIVE": "Sammelzahlung angelegt"
    }
  },
  "es": {
    "knownIban": "IBAN conocido",
    "collectiveSuggestion": "Pago colectivo · {count} partidas",
    "collectiveSuggestionHint": "Un pago, asignado primero a la partida con vencimiento más antiguo",
    "collectiveSum": "Suma {sum}",
    "collectiveDifference": "la diferencia {difference} queda sin asignar (Problemas)",
    "collectivePartial": "la última partida se paga solo en parte",
    "unknownCounterparty": "Contraparte desconocida",
    "noRemittance": "Sin concepto",
    "unconfirmedTarget": "La reserva aún no está confirmada; el pago se asignará igual.",
    "unconfirmedShort": "Reserva sin confirmar",
    "showCandidates": "Ver candidatos",
    "booking": "Reserva",
    "invoice": "Factura",
    "payoutLabel": "Pago al comercio",
    "amountLabels": {
      "open": "pendiente",
      "payment": "Pago"
    },
    "choose": "Elegir destino",
    "ignore": "Ignorar",
    "change": "Cambiar",
    "unignore": "Reabrir",
    "undo": "Deshacer",
    "undoHint": "Deshacer y volver a \"Por conciliar\"; un pago creado se mantiene",
    "distribute": "Abrir pago →",
    "counterpartyPlaceholder": "Contraparte",
    "confirmedAt": "Confirmado el {date}",
    "status": {
      "suggested": "Sugerencia",
      "candidates": "Posibles coincidencias",
      "none": "Sin coincidencia"
    },
    "hint": {
      "candidates": "Varios importes abiertos podrían encajar; revísalos.",
      "none": "No se encontró ningún importe abierto.",
      "confirmed": "Asignado",
      "ignored": "No se asignará."
    },
    "confirmAction": {
      "CREATE_PAYMENT": "Crear pago",
      "LINK_PAYMENT": "Vincular",
      "LINK_SETTLEMENT": "Vincular"
    },
    "confirmed": {
      "CREATE_PAYMENT": "Pago creado",
      "LINK_PAYMENT": "Pago vinculado",
      "LINK_SETTLEMENT": "Pago al comercio vinculado",
      "IGNORE": "Ignorado",
      "COLLECTIVE": "Pago colectivo creado"
    }
  }
}
</i18n>
